import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Badge, Modal, Form, Alert, Spinner, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import { GRADE_LEVELS } from '../utils/gradeLevels';
import axios from 'axios';
import './ContentLibrary.css';

const ContentLibrary = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState(['Black History']);
  const [editFormData, setEditFormData] = useState({
    tags: '',
    category: 'academic',
    isPublic: false,
    isShared: false
  });
  const [filters, setFilters] = useState({
    type: 'all',
    subject: '',
    gradeLevel: '',
    tags: '',
    search: ''
  });
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState(null);

  // Fetch available subjects (same as curriculum matcher and quiz creation)
  const fetchSubjects = async () => {
    try {
      const response = await axios.get(createApiUrl('/teacher/subjects'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAvailableSubjects(response.data.data || ['Black History']);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Keep default subjects on error
      setAvailableSubjects(['Black History']);
    }
  };

  useEffect(() => {
    fetchSubjects();
    fetchContent();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when filters change only
  }, [filters]);

  const fetchContent = async () => {
    try {
      setError(null); // Clear any previous errors
      const params = new URLSearchParams();
      if (filters.type !== 'all') params.append('type', filters.type);
      if (filters.subject) params.append('subject', filters.subject);
      if (filters.gradeLevel) params.append('gradeLevel', filters.gradeLevel);
      if (filters.tags) params.append('tags', filters.tags);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(createApiUrl(`/api/library?${params.toString()}`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Successfully fetched - set content (even if empty array)
        setContent(response.data.data.content || []);
      } else {
        // API returned success: false - this is an actual error
        setError('Failed to fetch content library');
      }
    } catch (err) {
      // Only show error for actual failures (network errors, 4xx, 5xx)
      // Don't show error for empty results (which would be a successful 200 response)
      if (err.response && err.response.status >= 400) {
        console.error('Error fetching content:', err);
        setError('Failed to fetch content library');
      } else if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        console.error('Network error fetching content:', err);
        setError('Network error. Please check your connection.');
      } else {
        // For other errors, just log but don't show error message
        // This handles cases where the API might return empty results
        console.error('Error fetching content:', err);
        setContent([]); // Set empty array instead of showing error
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(createApiUrl('/api/library/stats'), {
        withCredentials: true
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditFormData({
      tags: item.tags ? item.tags.join(', ') : '',
      category: item.category || 'academic',
      isPublic: item.isPublic || false,
      isShared: item.isShared || false
    });
    setShowEditModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const tagsArray = editFormData.tags 
        ? editFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      const response = await axios.put(
        createApiUrl(`/api/library/${selectedItem.type}/${selectedItem._id}`),
        {
          tags: tagsArray,
          category: editFormData.category,
          isPublic: editFormData.isPublic,
          isShared: editFormData.isShared
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setShowEditModal(false);
        fetchContent(); // Refresh the content
        setError(null);
      } else {
        setError('Failed to update library item');
      }
    } catch (err) {
      console.error('Error updating library item:', err);
      setError('Failed to update library item');
    }
  };

  const handleCreateFromLibrary = (item) => {
    setSelectedItem(item);
    setShowCreateModal(true);
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDeleteItem = async () => {
    if (!selectedItem) return;

    try {
      const response = await axios.delete(createApiUrl(`/api/library/${selectedItem.contentType}/${selectedItem._id}`), {
        data: {
          reason: 'Teacher requested deletion'
        },
        withCredentials: true
      });

      if (response.data.success) {
        setContent(content.filter(item => item._id !== selectedItem._id));
        setShowDeleteModal(false);
        setSelectedItem(null);
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item');
    }
  };

  const confirmCreateFromLibrary = async () => {
    if (!selectedItem) return;

    try {
      const response = await axios.post(createApiUrl(`/api/library/${selectedItem.contentType}/${selectedItem._id}/create`), {
        newTitle: `${selectedItem.title} (Copy)`
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setShowCreateModal(false);
        setSelectedItem(null);
        // Optionally refresh content or show success message
      }
    } catch (err) {
      console.error('Error creating from library:', err);
      setError('Failed to create from library');
    }
  };

  const getTypeIcon = (type) => {
    return type === 'quiz' ? '❓' : '📋';
  };

  const getTypeBadge = (type) => {
    return type === 'quiz' ? 
      <Badge bg="info">Quiz</Badge> : 
      <Badge bg="success">Mission</Badge>;
  };

  const getDifficultyBadge = (difficulty) => {
    const difficultyConfig = {
      beginner: { variant: 'success', text: 'Beginner' },
      intermediate: { variant: 'warning', text: 'Intermediate' },
      advanced: { variant: 'danger', text: 'Advanced' }
    };
    
    const config = difficultyConfig[difficulty] || difficultyConfig.intermediate;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getUsageCount = (item) => {
    return item.usageHistory ? item.usageHistory.length : 0;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading content library...</p>
      </div>
    );
  }

  return (
    <div className="content-library">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Content Library</h3>
        <div className="library-actions">
          <Button variant="primary" className="me-2">
            Create New Content
          </Button>
          <Button variant="outline-secondary">
            Import from Previous Year
          </Button>
        </div>
      </div>

      {stats && (
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-card-body">
              <h4 className="stat-number">{stats.quizzes.totalQuizzes}</h4>
              <p className="stat-label">Quizzes</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-body">
              <h4 className="stat-number">{stats.missions.totalMissions}</h4>
              <p className="stat-label">Missions</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-body">
              <h4 className="stat-number">{stats.quizzes.totalUsage + stats.missions.totalUsage}</h4>
              <p className="stat-label">Total Uses</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-body">
              <h4 className="stat-number">
                {Math.round((stats.quizzes.averageScore + stats.missions.averageScore) / 2)}%
              </h4>
              <p className="stat-label">Avg Score</p>
            </div>
          </div>
        </div>
      )}

      <div className="library-filters">
        <div className="filters-form">
          <div className="filters-row">
            <div className="filter-group">
              <label className="filter-label">Subject</label>
              <select
                className="filter-select"
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
              >
                <option value="">All Subjects</option>
                {availableSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Grade Level</label>
              <select
                className="filter-select"
                value={filters.gradeLevel}
                onChange={(e) => handleFilterChange('gradeLevel', e.target.value)}
              >
                <option value="">All Grades</option>
                {GRADE_LEVELS.map(grade => (
                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Tags</label>
              <input
                type="text"
                className="filter-input"
                placeholder="e.g., math, history"
                value={filters.tags}
                onChange={(e) => handleFilterChange('tags', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search content..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-buttons">
              <button 
                type="button"
                className="filter-btn filter-btn-secondary"
                onClick={() => setFilters({ type: 'all', subject: '', gradeLevel: '', tags: '', search: '' })}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Tab eventKey="all" title="All Content">
          <ContentGrid 
            content={content} 
            onCreate={handleCreateFromLibrary}
            onDelete={handleDeleteItem}
            onEdit={handleEdit}
            onView={handleView}
            getTypeIcon={getTypeIcon}
            getTypeBadge={getTypeBadge}
            getDifficultyBadge={getDifficultyBadge}
            formatDate={formatDate}
            getUsageCount={getUsageCount}
          />
        </Tab>
        <Tab eventKey="quizzes" title="Quizzes">
          <ContentGrid 
            content={content.filter(item => item.contentType === 'quiz')} 
            onCreate={handleCreateFromLibrary}
            onDelete={handleDeleteItem}
            onEdit={handleEdit}
            onView={handleView}
            getTypeIcon={getTypeIcon}
            getTypeBadge={getTypeBadge}
            getDifficultyBadge={getDifficultyBadge}
            formatDate={formatDate}
            getUsageCount={getUsageCount}
          />
        </Tab>
        <Tab eventKey="missions" title="Missions">
          <ContentGrid 
            content={content.filter(item => item.contentType === 'mission')} 
            onCreate={handleCreateFromLibrary}
            onDelete={handleDeleteItem}
            onEdit={handleEdit}
            onView={handleView}
            getTypeIcon={getTypeIcon}
            getTypeBadge={getTypeBadge}
            getDifficultyBadge={getDifficultyBadge}
            formatDate={formatDate}
            getUsageCount={getUsageCount}
          />
        </Tab>
      </Tabs>

      {/* Create from Library Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create from Library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Create a new {selectedItem?.contentType} from "{selectedItem?.title}"?</p>
          <Form.Group>
            <Form.Label>New Title</Form.Label>
            <Form.Control
              type="text"
              defaultValue={`${selectedItem?.title} (Copy)`}
              placeholder="Enter new title..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmCreateFromLibrary}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Item Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete from Library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{selectedItem?.title}" from your library?</p>
          <Alert variant="warning">
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteItem}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Permissions Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Library Permissions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              <div className="mb-3">
                <h5>{selectedItem.title}</h5>
                <p className="text-muted">{selectedItem.description}</p>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Tags (comma-separated)</label>
                <input
                  type="text"
                  className="form-control"
                  value={editFormData.tags}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g., math, algebra, equations"
                />
                <small className="form-text text-muted">
                  Add tags to help you find this content later
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={editFormData.category}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="academic">Academic</option>
                  <option value="assessment">Assessment</option>
                  <option value="practice">Practice</option>
                  <option value="review">Review</option>
                  <option value="homework">Homework</option>
                </select>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editIsPublic"
                    checked={editFormData.isPublic}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="editIsPublic">
                    Make this content public (visible to other teachers)
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editIsShared"
                    checked={editFormData.isShared}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, isShared: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="editIsShared">
                    Share with school colleagues
                  </label>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Update Permissions
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Details Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Content Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              <div className="mb-4">
                <h4>{selectedItem.title}</h4>
                <p className="text-muted">{selectedItem.description}</p>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Type:</strong> {selectedItem.contentType === 'quiz' ? 'Quiz' : 'Mission'}
                </div>
                <div className="col-md-6">
                  <strong>Subject:</strong> {selectedItem.subject}
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Grade Level:</strong> {selectedItem.gradeLevel}
                </div>
                <div className="col-md-6">
                  <strong>Difficulty:</strong> {selectedItem.difficulty}
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Created:</strong> {formatDate(selectedItem.createdAt)}
                </div>
                <div className="col-md-6">
                  <strong>Usage Count:</strong> {getUsageCount(selectedItem)}
                </div>
              </div>
              
              {selectedItem.tags && selectedItem.tags.length > 0 && (
                <div className="mb-3">
                  <strong>Tags:</strong>
                  <div className="mt-1">
                    {selectedItem.tags.map((tag, index) => (
                      <span key={index} className="badge bg-secondary me-1">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mb-3">
                <strong>Permissions:</strong>
                <div className="mt-1">
                  <span className={`badge ${selectedItem.isPublic ? 'bg-success' : 'bg-secondary'} me-2`}>
                    {selectedItem.isPublic ? 'Public' : 'Private'}
                  </span>
                  <span className={`badge ${selectedItem.isShared ? 'bg-info' : 'bg-secondary'}`}>
                    {selectedItem.isShared ? 'Shared' : 'Not Shared'}
                  </span>
                </div>
              </div>
              
              {selectedItem.contentType === 'quiz' && selectedItem.questions && (
                <div className="mt-4">
                  <h5>Questions ({selectedItem.questions.length})</h5>
                  <div className="list-group">
                    {selectedItem.questions.map((question, index) => (
                      <div key={question._id || index} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1">
                            <h6 className="mb-2">Question {index + 1} ({question.type}) - {question.points} points</h6>
                            <p className="mb-2">{question.question}</p>
                          </div>
                        </div>

                        {/* Question Options (for multiple choice) */}
                        {question.type === 'multiple-choice' && question.options && question.options.length > 0 && (
                          <div className="mb-3">
                            <strong>Options:</strong>
                            <ul className="list-unstyled mt-2">
                              {question.options.map((option, optIndex) => (
                                <li key={optIndex} className={`p-2 rounded mb-1 ${optIndex === question.correctAnswer ? 'bg-success text-white' : 'bg-light'}`}>
                                  <span className="me-2">{String.fromCharCode(65 + optIndex)}.</span>
                                  {option}
                                  {optIndex === question.correctAnswer && <span className="ms-2">✓ Correct Answer</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* True/False Answer */}
                        {question.type === 'true-false' && (
                          <div className="mb-3">
                            <strong>Correct Answer:</strong>
                            <div className={`p-2 rounded mt-2 ${question.correctAnswer ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                              {question.correctAnswer ? 'True' : 'False'} ✓
                            </div>
                          </div>
                        )}

                        {/* Fill in the Blank Answer */}
                        {question.type === 'fill-blank' && question.correctText && (
                          <div className="mb-3">
                            <strong>Correct Answer:</strong>
                            <div className="p-2 bg-success text-white rounded mt-2">
                              {question.correctText} ✓
                            </div>
                          </div>
                        )}

                        {/* Short Answer */}
                        {question.type === 'short-answer' && question.correctAnswer && (
                          <div className="mb-3">
                            <strong>Correct Answer:</strong>
                            <div className="p-2 bg-success text-white rounded mt-2">
                              {question.correctAnswer} ✓
                            </div>
                          </div>
                        )}

                        {/* Essay Question */}
                        {question.type === 'essay' && (
                          <div className="mb-3">
                            <strong>Essay Question</strong>
                            <div className="p-2 bg-info text-white rounded mt-2">
                              This is an essay question. Students will provide written responses.
                            </div>
                            {question.rubric && question.rubric.criteria && question.rubric.criteria.length > 0 && (
                              <div className="mt-2">
                                <strong>Grading Rubric:</strong>
                                <ul className="list-unstyled mt-2">
                                  {question.rubric.criteria.map((criteria, criteriaIndex) => (
                                    <li key={criteriaIndex} className="p-2 bg-light rounded mb-1">
                                      <strong>{criteria.name}:</strong> {criteria.description} ({criteria.maxPoints} points)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Explanation */}
                        {question.explanation && (
                          <div className="mb-3">
                            <strong>Explanation:</strong>
                            <div className="p-2 bg-light rounded mt-2">
                              {question.explanation}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedItem.contentType === 'mission' && selectedItem.tasks && (
                <div className="mb-3">
                  <strong>Tasks ({selectedItem.tasks.length}):</strong>
                  <div className="mt-2">
                    {selectedItem.tasks.map((task, index) => (
                      <div key={index} className="card mb-2">
                        <div className="card-body p-2">
                          <div className="fw-bold">Task {index + 1}: {task.title}</div>
                          <div className="text-muted small">{task.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const ContentGrid = ({ 
  content, 
  onCreate, 
  onDelete,
  onEdit,
  onView, 
  getTypeIcon, 
  getTypeBadge, 
  getDifficultyBadge, 
  formatDate, 
  getUsageCount 
}) => {
  if (content.length === 0) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <h5 className="text-muted">No content found</h5>
          <p className="text-muted">Try adjusting your filters or create new content</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row>
      {content.map(item => (
        <Col key={item._id} md={6} lg={4} className="mb-4">
          <Card className="library-item-card h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <span className="me-2">{getTypeIcon(item.contentType)}</span>
                {getTypeBadge(item.contentType)}
              </div>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Actions
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => onCreate(item)}>
                    Create New
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onEdit(item)}>Edit Permissions</Dropdown.Item>
                  <Dropdown.Item onClick={() => onView(item)}>View Details</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item 
                    className="text-danger"
                    onClick={() => onDelete(item)}
                  >
                    Delete
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Card.Header>
            <Card.Body>
              <h5 className="card-title">{item.title}</h5>
              <p className="card-text text-muted">{item.description}</p>
              
              <div className="item-meta mb-3">
                <Row>
                  <Col xs={6}>
                    <small className="text-muted">Subject</small>
                    <div>{item.subject}</div>
                  </Col>
                  <Col xs={6}>
                    <small className="text-muted">Grade</small>
                    <div>{item.gradeLevel}</div>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col xs={6}>
                    <small className="text-muted">Difficulty</small>
                    <div>{getDifficultyBadge(item.difficulty)}</div>
                  </Col>
                  <Col xs={6}>
                    <small className="text-muted">Used</small>
                    <div>{getUsageCount(item)} times</div>
                  </Col>
                </Row>
              </div>

              {item.tags && item.tags.length > 0 && (
                <div className="item-tags mb-3">
                  {item.tags.slice(0, 3).map(tag => (
                    <Badge key={tag} bg="secondary" className="me-1">
                      {tag}
                    </Badge>
                  ))}
                  {item.tags.length > 3 && (
                    <Badge bg="light" text="dark">
                      +{item.tags.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
            </Card.Body>
            <Card.Footer className="text-muted">
              <small>Created: {formatDate(item.originalCreatedAt)}</small>
              {item.lastUsedAt && (
                <small className="ms-2">Last used: {formatDate(item.lastUsedAt)}</small>
              )}
            </Card.Footer>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default ContentLibrary;
